import { type CSSProperties, type Ref, useEffect, useEffectEvent, useMemo, useState } from 'react';
import { createCalendar } from '@internationalized/date';
import { useCalendarState } from '@react-stately/calendar';
import { clsx } from 'clsx';
import { type DateValue, type CalendarProps as PrimitiveCalendarProps, useCalendar } from 'react-aria';
import { useCurrentDate } from '../../../../util';
import { VerticalStack } from '../../../alignment/vertical-stack';
import type { DatePickerPagination } from '../util';
import { CalendarGrid } from './grid/calendar-grid';
import { CalendarHeader } from './header/calendar-header';
import { MonthGrid } from './month-grid/month-grid';
import type { CalendarMode } from './util';
import { YearGrid } from './year-grid/year-grid';
import styles from './calendar.module.scss';

const YEAR_SELECT_RANGE_COUNT = 28;

interface CalendarProps extends PrimitiveCalendarProps<DateValue> {
  className?: string;
  isOpen: boolean;
  locale: string;
  pagination?: DatePickerPagination;
  ref?: Ref<HTMLDivElement>;
  style?: CSSProperties;
  timeZone: string;
}

export function Calendar({ className, isOpen, locale, pagination, ref, style, timeZone, ...props }: CalendarProps) {
  const { zonedDate } = useCurrentDate(timeZone);
  const [mode, setMode] = useState<CalendarMode>('day');
  const state = useCalendarState({
    ...props,
    defaultValue: props.defaultValue ?? zonedDate,
    locale,
    createCalendar,
  });
  const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(props, state);
  const mergedStyle = useMemo(
    () => ({
      ...calendarProps.style,
      ...style,
    }),
    [calendarProps.style, style],
  );

  const { focusedDate, setFocusedDate, value } = state;
  const valueParts = useMemo(() => {
    if (!value?.year || !value?.month || !value?.day) {
      return null;
    }

    return {
      year: value.year,
      month: value.month,
      day: value.day,
    } as const;
  }, [value?.year, value?.month, value?.day]);

  const updateFocusedDate = useEffectEvent((parts: typeof valueParts) => {
    if (parts) {
      setFocusedDate(focusedDate.set(parts));
    }
  });

  useEffect(() => {
    if (isOpen && valueParts) {
      updateFocusedDate(valueParts);
    }
  }, [isOpen, valueParts]);

  return (
    <VerticalStack {...calendarProps} className={clsx(styles.calendar, className)} ref={ref} style={mergedStyle}>
      <CalendarHeader
        buttonPropsNext={nextButtonProps}
        buttonPropsPrev={prevButtonProps}
        focusedDate={state.focusedDate}
        mode={mode}
        setFocusedDate={setFocusedDate}
        setMode={setMode}
        yearSelectOptionCount={YEAR_SELECT_RANGE_COUNT}
      />
      {mode === 'day' ? <CalendarGrid locale={locale} pagination={pagination} state={state} /> : null}
      {mode === 'month' ? (
        <MonthGrid
          focusedMonth={state.focusedDate.month}
          onSelectMonth={(month) => {
            setFocusedDate(focusedDate.set({ day: 1, month }));
            setMode('day');
          }}
        />
      ) : null}
      {mode === 'year' ? (
        <YearGrid
          focusedYear={state.focusedDate.year}
          onSelectYear={(year) => {
            setFocusedDate(focusedDate.set({ day: 1, year }));
            setMode('day');
          }}
          yearOptions={YEAR_SELECT_RANGE_COUNT}
        />
      ) : null}
    </VerticalStack>
  );
}
